import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError, of, tap, filter } from 'rxjs';
import { retry, catchError } from 'rxjs/operators';
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service';
import { AppConfigService } from 'src/app/services/AppConfigService';

/** Zero-pads a number to the specified width. Used for ISO date string formatting. */
const zeroPad = (num, places) => String(num).padStart(places, '0');

/** HTTP options for ERC API calls (form-encoded body, JSON response). */
const ercHttpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/x-www-form-urlencoded',
    'accept': 'application/json'
  })
};

/**
 * Exchange rate record returned by the National Bank of Ukraine (NBU) API,
 * proxied through the backend `/ExternalApi/GetNBURate` endpoint.
 */
export interface nbuRateItem {
  /** Exchange date in `DD.MM.YYYY` format. */
  exchangedate: string;
  /** ISO 4217 numeric currency code. */
  r030: string;
  /** ISO 4217 alphabetic currency code (e.g. `USD`, `EUR`). */
  cc: string;
  /** Currency name in Ukrainian. */
  txt: string;
  /** Currency name in English (not always populated). */
  enname?: string;
  /** Exchange rate value as a string. */
  rate?: string;
  /** Number of currency units the rate applies to (e.g. `1`, `100`). */
  units?: string;
  /** Rate per single unit, computed from `rate / units`. */
  rate_per_unit?: string;
  /** NBU currency group classification. */
  group?: string;
  /** Date from which the rate is effective. */
  calcdate?: string;
}

/** One interbank rate point returned by `GET /ExternalApi/GetMinfinMbRate` (minfin.ua `mb` feed), keyed by WebYTO `CURCODE`. */
export interface minfinMbPoint {
  /** The price the server picked (`MINFIN_PRICE_FIELD`, default `ask`). */
  value: number;
  bid: number | null;
  ask: number | null;
  /** Slot time of the chosen point, Europe/Kyiv local, as sent by the vendor. */
  slot: string;
  pointDate: string;
}

/** Response of `GET /ExternalApi/GetMinfinMbRate`. */
export interface minfinMbRateResponse {
  priceField: string;
  /** `previous-close`: the feed of the previous business day, stamped with `rateDate`; `as-asked`: the feed of `requestedDate`. */
  basis: 'previous-close' | 'as-asked';
  /** False on a Saturday or Sunday with `basis=previous-close`: there is no rate to write, `points` is empty. */
  businessDay: boolean;
  requestedDate: string | null;
  /** The Europe/Kyiv day (`YYYY-MM-DD`) a row must be stamped with; never use the browser clock for it. */
  rateDate: string;
  points: Record<string, minfinMbPoint>;
  /** `CURCODE`s the feed returned nothing usable for. */
  missing: string[];
  skipped: Array<{ curcode: string; slot: string; reason: string }>;
}

/**
 * REST client for external financial data sources:
 * - **NBU** (National Bank of Ukraine) — official UAH exchange rates, proxied
 *   through the backend `/ExternalApi` route to avoid CORS restrictions.
 * - **ERC** — a vendor-negotiated commercial currency rate source (`Currencies`
 *   rows like `USD_ERC` sit alongside `USD_KVA`/`USD_ASB`/`USD_ELK`/`USD_TLTN`,
 *   all vendor/bank-specific rate variants — see `WORKLOG.md`, 2026-07-12).
 *   What "ERC" actually stands for isn't documented anywhere in this codebase;
 *   an earlier version of this comment guessed "Ukrainian Energy Regulatory
 *   Commission," which is wrong (owner, 2026-09-14: it's a large IT
 *   distributor's abbreviation, not a government body).
 *
 * All requests are routed through the backend API server (not called directly
 * from the browser) to centralise CORS handling and optional caching.
 *
 * @remarks
 * ERC credentials live server-side only (`ERC_CREDENTIALS` in the backend's
 * `.env.secrets` — see `webyto_api-express4-TS-20250331/routes/ExternalApi.ts`),
 * never in this service. This client never sends or holds them (2026-09-14,
 * `SEC-CR4`/`B-35` — an earlier version hardcoded a credential here that the
 * backend never even read, see `technical-backlog.md`).
 */
@Injectable({
  providedIn: 'root'
})
export class ExternalApiService {
  apiURL: string = '';
  /** Backend API path prefix for all external data proxy endpoints. */
  apiPath: string = '/ExternalApi';

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    })
  };

  constructor(
    private http: HttpClient,
    private globalsettingsservice: GetglobalsettingsService,
    private appConfigService: AppConfigService
  ) {
    // Initialize API URL from runtime config
    const config = this.appConfigService.getConfig();
  }

  /**
   * Builds an absolute URL to the backend external API proxy endpoint.
   *
   * @param url - Path suffix after `/ExternalApi` (e.g. `/GetNBURate/USD/20260101`).
   * @returns Absolute URL to the backend proxy endpoint.
   */
  private _getApi1Url(url: string = ''): string {
    let _api: string = this.globalsettingsservice.getApiServerUrl();
    return _api + this.apiPath + url;
  }

  // ---------------------------------------------------------------------------
  // NBU (National Bank of Ukraine) currency rates
  // ---------------------------------------------------------------------------

  /**
   * Returns the NBU official exchange rate for a single currency on a given
   * date, proxied through the backend.
   *
   * Calls `GET /ExternalApi/GetNBURate/:currency/:date` where date is
   * formatted as `YYYYMMDD`.
   *
   * @param currency - ISO 4217 alphabetic code (e.g. `'USD'`, `'EUR'`).
   * @param date - Rate date. Defaults to today.
   * @returns Observable emitting a one-element `nbuRateItem[]` array.
   */
  getNBUCurrencyRate(currency: string, date: Date = new Date()): Observable<nbuRateItem[]> {
    let d: string = zeroPad(date.getFullYear(), 4) + zeroPad(date.getMonth() + 1, 2) + zeroPad(date.getDate(), 2);
    let v: string = currency;
    return this.http.get<nbuRateItem[]>(
      this._getApi1Url('/GetNBURate/' + v + '/' + d), { ...this.httpOptions })
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  // ---------------------------------------------------------------------------
  // minfin.ua interbank (`mb`) rates
  // ---------------------------------------------------------------------------

  /**
   * Returns the minfin.ua interbank rate of every currency the server maps to the `MB:` feed, proxied through the
   * backend (the key and the currency map stay server-side).
   *
   * Calls `GET /ExternalApi/GetMinfinMbRate[?date=YYYY-MM-DD]`. No `retry()` and no `handleError`: a vendor 403 can
   * mean an exhausted request quota, so repeating is harmful, and the caller shows the server's `error` text
   * (503 not configured, 502 vendor failure) in its log instead of an alert.
   *
   * @param date - Optional day `YYYY-MM-DD`; omitted means today's feed (not the rate of today: use `basis`).
   * @param basis - `previous-close`: the rate of today is the previous business day's last price (what the rate table means);
   *   the server picks the day from its Kyiv clock and cannot be combined with `date`.
   * @returns Observable emitting the points keyed by WebYTO `CURCODE`, plus `rateDate` to stamp the rows with.
   */
  getMinfinMbRate(date?: string, basis?: 'previous-close'): Observable<minfinMbRateResponse> {
    let params = new HttpParams();
    if (date) { params = params.set('date', date); }
    if (basis) { params = params.set('basis', basis); }
    return this.http.get<minfinMbRateResponse>(this._getApi1Url('/GetMinfinMbRate'), { ...this.httpOptions, params });
  }

  /**
   * Returns historical NBU exchange rates for a single currency over a date
   * range, proxied through the backend.
   *
   * Calls `GET /ExternalApi/GetNbuRateHistory/:currency/:fromdate/:todate`
   * where dates are formatted as `YYYYMMDD`.
   *
   * @param currency - ISO 4217 alphabetic code (e.g. `'USD'`).
   * @param fromdate - Start of the date range. Defaults to today.
   * @param todate - End of the date range. Defaults to today.
   * @returns Observable emitting an array of `nbuRateItem` records, one per day.
   */
  getNBUCurrencyRateHistory(currency: string, fromdate: Date = new Date(), todate: Date = new Date()): Observable<nbuRateItem[]> {
    let f: string = zeroPad(fromdate.getFullYear(), 4) + zeroPad(fromdate.getMonth() + 1, 2) + zeroPad(fromdate.getDate(), 2);
    let t: string = zeroPad(todate.getFullYear(), 4) + zeroPad(todate.getMonth() + 1, 2) + zeroPad(todate.getDate(), 2);
    let v: string = currency;
    return this.http.get<nbuRateItem[]>(
      this._getApi1Url('/GetNbuRateHistory/' + v + '/' + f + '/' + t), { ...this.httpOptions })
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  // ---------------------------------------------------------------------------
  // ERC currency rates (vendor short code — see the class doc comment above)
  // ---------------------------------------------------------------------------

  /**
   * Returns ERC currency rates for the given date.
   *
   * Calls `POST /ExternalApi/GetErcRate/?_date=<actual>`.
   *
   * If the response contains `IsError: true`, the Observable emits an error.
   *
   * @param actual - Rate date to query.
   * @returns Observable emitting the ERC rate response, or erroring if
   *   the server returns `IsError: true`.
   */
  getErcCurrencyrates(actual: Date): Observable<any> {
    return this.http.post<any>(this._getApi1Url('/GetErcRate/?_date=' + (actual ? new Date(actual) : new Date()).toISOString()), { ...ercHttpOptions })
      .pipe(
        catchError(this.handleError),
        filter(data => (data)),
        tap(data => {
          if (('IsError' in data) && data.IsError) {
            return throwError(() => data);
          }
        }),
        tap(data => console.log('*** ExternalApiService getErcCurrencyrates: ' + actual + ': ' + JSON.stringify(data))),
      );
  }

  // ---------------------------------------------------------------------------
  // Error handling
  // ---------------------------------------------------------------------------

  /**
   * Centralised HTTP error handler used by all methods in this service.
   *
   * Extracts a human-readable message, shows a browser `alert` (legacy pattern
   * — Phase 1 will replace with a toast), and re-throws so the calling
   * Observable terminates.
   *
   * @param error - Angular `HttpErrorResponse` or `ErrorEvent`.
   * @returns An Observable that immediately errors with the formatted message.
   */
  handleError(error) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      errorMessage = error.error.message;
    } else {
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    window.alert(errorMessage);
    return throwError(() => errorMessage);
  }
}
